import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  createdAt: string;
}

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleJwtPayload {
  sub: string;
  email: string;
  name: string;
  picture?: string;
  given_name?: string;
  family_name?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  handleGoogleSuccess: (credentialResponse: GoogleCredentialResponse) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = "insura_auth_user";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem(AUTH_STORAGE_KEY);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    // For demo purposes, accept any email/password with basic validation
    // In production, this would call your backend API
    if (!email || !password) {
      setIsLoading(false);
      throw new Error("Email and password are required");
    }

    if (password.length < 6) {
      setIsLoading(false);
      throw new Error("Invalid credentials");
    }

    // Check if user exists in localStorage (from signup)
    const existingUsers = JSON.parse(
      localStorage.getItem("insura_users") || "[]"
    );
    const existingUser = existingUsers.find((u: any) => u.email === email);

    if (existingUser) {
      // Verify password (in real app, this would be done server-side)
      if (existingUser.password !== password) {
        setIsLoading(false);
        throw new Error("Invalid credentials");
      }

      const userData: User = {
        id: existingUser.id,
        email: existingUser.email,
        name: existingUser.name,
        picture: existingUser.picture,
        createdAt: existingUser.createdAt,
      };

      setUser(userData);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
    } else {
      // Create new user on first login (demo mode)
      const userData: User = {
        id: `user_${Date.now()}`,
        email,
        name: email.split("@")[0],
        createdAt: new Date().toISOString(),
      };

      setUser(userData);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));

      // Save to users list
      existingUsers.push({ ...userData, password });
      localStorage.setItem("insura_users", JSON.stringify(existingUsers));
    }

    setIsLoading(false);
  };

  const signup = async (
    name: string,
    email: string,
    password: string
  ): Promise<void> => {
    setIsLoading(true);

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    // Validation
    if (!name || !email || !password) {
      setIsLoading(false);
      throw new Error("All fields are required");
    }

    if (password.length < 6) {
      setIsLoading(false);
      throw new Error("Password must be at least 6 characters");
    }

    if (!email.includes("@")) {
      setIsLoading(false);
      throw new Error("Please enter a valid email address");
    }

    // Check if user already exists
    const existingUsers = JSON.parse(
      localStorage.getItem("insura_users") || "[]"
    );
    if (existingUsers.find((u: any) => u.email === email)) {
      setIsLoading(false);
      throw new Error("An account with this email already exists");
    }

    // Create new user
    const userData: User = {
      id: `user_${Date.now()}`,
      email,
      name,
      createdAt: new Date().toISOString(),
    };

    // Save to users list
    existingUsers.push({ ...userData, password });
    localStorage.setItem("insura_users", JSON.stringify(existingUsers));

    // Log in the new user
    setUser(userData);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
    setIsLoading(false);
  };

  const handleGoogleSuccess = (
    credentialResponse: GoogleCredentialResponse
  ) => {
    try {
      // Decode the JWT credential from Google
      const decoded = jwtDecode<GoogleJwtPayload>(
        credentialResponse.credential
      );

      const userData: User = {
        id: `google_${decoded.sub}`,
        email: decoded.email,
        name: decoded.name || decoded.given_name || decoded.email.split("@")[0],
        picture: decoded.picture,
        createdAt: new Date().toISOString(),
      };

      // Save user
      setUser(userData);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));

      // Add to users list if not exists
      const existingUsers = JSON.parse(
        localStorage.getItem("insura_users") || "[]"
      );
      const existingIndex = existingUsers.findIndex(
        (u: any) => u.email === userData.email
      );

      if (existingIndex === -1) {
        existingUsers.push({ ...userData, provider: "google" });
      } else {
        // Update existing user with Google info
        existingUsers[existingIndex] = {
          ...existingUsers[existingIndex],
          ...userData,
          provider: "google",
        };
      }
      localStorage.setItem("insura_users", JSON.stringify(existingUsers));

      console.log("✅ Google login successful:", userData.email);
    } catch (error) {
      console.error("❌ Failed to decode Google credential:", error);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        handleGoogleSuccess,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
